/**
 * DI-3-D — the key a row's persisted hashes are filed under
 * (`ClusterIntake.rowHashes`).
 *
 * DI-3-P2 — moved here from `session/sessionStore.ts` (which re-exports it)
 * so the index worker can match a job against the persisted hashes with the
 * SAME formula: a renamed or re-sized file is a different key and is hashed
 * again. Pure — imported by the worker, the main thread and Vitest.
 *
 * DI-3-S15-FE (smoke R2-F3) — the key is the file's BASE NAME + size, never
 * its relative path: the same card folder reads `x.JPG` from a drop of loose
 * files, `Upload/x.JPG` from a dropped or picked folder and
 * `root/Upload/x.JPG` from a folder under the field-data root. Two files of
 * one drop with the same name and size but different content cannot share a
 * key: the key is left out (`hashRecordOf`, `knownHashesOf`), so both are
 * hashed again and the hash decides. Records written before this story
 * (keys with a `/`) are read through their base name (`knownHashesOf`) — no
 * store rewrite.
 */
import type { HashResult } from "./types.js";

/** The last segment of a relative path (`/` or `\`). */
export function baseName(path: string): string {
  const cut = Math.max(path.lastIndexOf("/"), path.lastIndexOf("\\"));
  return cut < 0 ? path : path.slice(cut + 1);
}

export function rowHashKey(row: {
  relativePath: string;
  size: number;
}): string {
  return `${baseName(row.relativePath)}|${String(row.size)}`;
}

/**
 * A stored key read the way this story files it: `a/b/x.JPG|123` →
 * `x.JPG|123`; a key already of that form is returned unchanged.
 */
export function normalizedHashKey(key: string): string {
  const bar = key.lastIndexOf("|");
  if (bar < 0) return baseName(key);
  return `${baseName(key.slice(0, bar))}${key.slice(bar)}`;
}

function sameHashes(a: HashResult, b: HashResult): boolean {
  return a.md5Hex === b.md5Hex && a.payloadSha256Hex === b.payloadSha256Hex;
}

/**
 * Hashes by `rowHashKey`, where a key that two DIFFERENT hashes claim is
 * left out (a same-name, same-size, different-content pair: the hash
 * decides, so neither may reuse the other's). Equal hashes under one key (a
 * true duplicate) keep it.
 */
function collect(
  entries: Iterable<readonly [string, HashResult]>,
): Record<string, HashResult> {
  const out = new Map<string, HashResult>();
  const ambiguous = new Set<string>();
  for (const [key, h] of entries) {
    if (ambiguous.has(key)) continue;
    const prior = out.get(key);
    if (prior && !sameHashes(prior, h)) {
      out.delete(key);
      ambiguous.add(key);
      continue;
    }
    if (!prior) out.set(key, h);
  }
  return Object.fromEntries(out);
}

/** The record a set's round trip persists (`ClusterIntake.rowHashes`). */
export function hashRecordOf(
  rows: Iterable<{
    relativePath: string;
    size: number;
    hashes: Pick<HashResult, "md5Hex" | "payloadSha256Hex">;
  }>,
): Record<string, HashResult> {
  const entries: [string, HashResult][] = [];
  for (const r of rows)
    entries.push([
      rowHashKey(r),
      {
        md5Hex: r.hashes.md5Hex,
        payloadSha256Hex: r.hashes.payloadSha256Hex,
        scanStart: null,
      },
    ]);
  return collect(entries);
}

/**
 * The known hashes of a session for a re-read (DI-3-P2's `knownHashes`):
 * every cluster's `rowHashes`, each key read through `normalizedHashKey` (a
 * pre-S15 record keyed by path is found by its base name); a key the
 * records disagree on is left out.
 */
export function knownHashesOf(
  records: Iterable<Record<string, HashResult> | undefined>,
): Record<string, HashResult> {
  const entries: [string, HashResult][] = [];
  for (const rec of records)
    for (const [k, h] of Object.entries(rec ?? {}))
      entries.push([normalizedHashKey(k), h]);
  return collect(entries);
}

/**
 * The known hashes a re-read of THIS folder may reuse: a key that more than
 * one file of the folder carries (same name and size in two sub-folders) is
 * left out, so each of those files is hashed and its own hash decides.
 */
export function knownForFolder(
  known: Record<string, HashResult>,
  files: Iterable<{ relativePath: string; size: number }>,
): Record<string, HashResult> {
  const seen = new Map<string, number>();
  for (const f of files) {
    const k = rowHashKey(f);
    seen.set(k, (seen.get(k) ?? 0) + 1);
  }
  const out: Record<string, HashResult> = {};
  for (const [k, h] of Object.entries(known)) if (seen.get(k) === 1) out[k] = h;
  return out;
}
